import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { formatAddress } from '../services/wallet'
import { getExplorerBaseUrl, formatDate } from '../utils/formatters'
import { getUserByWallet, getMe, updateMyProfile, changeMyPassword } from '../services/api'
import Section from '../components/m1nt/Section'
import OwnerSignedOut from '../components/m1nt/OwnerSignedOut'
import { M1ntButton, M1ntNotice, M1NT_INPUT_CLASS, M1NT_LABEL_CLASS } from '../components/m1nt/ui'
import { useAuthSession } from '../contexts/AuthSessionContext'

export default function Profile() {
  const { wallet, emailAuthToken, onboardingToken } = useAuthSession()
  const navigate = useNavigate()
  const [user, setUser] = useState(null)
  const [loading, setLoading] = useState(true)
  const [isEditing, setIsEditing] = useState(false)
  const [formData, setFormData] = useState({ name: '', email: '', wallet: '' })
  const [saving, setSaving] = useState(false)
  const [feedback, setFeedback] = useState(null)
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  })
  const [changingPassword, setChangingPassword] = useState(false)
  const [passwordFeedback, setPasswordFeedback] = useState(null)

  useEffect(() => {
    const canLoad = (wallet || emailAuthToken) && !onboardingToken
    if (canLoad) {
      setLoading(true)
      fetchUserData().finally(() => setLoading(false))
    } else {
      setUser(null)
      setLoading(false)
    }
  }, [wallet, emailAuthToken, onboardingToken])

  const fetchUserData = async () => {
    try {
      const res = wallet ? await getUserByWallet(wallet) : await getMe()
      const userData = res.data.data
      setUser(userData)
      setFormData({
        name: userData.name || '',
        email: userData.email || '',
        wallet: userData.wallet || '',
      })
    } catch {
      console.log('User not found in database')
      setUser(null)
    }
  }

  const handleSave = async () => {
    if (!wallet && !emailAuthToken) return

    setSaving(true)
    setFeedback(null)
    try {
      const nextWallet = String(formData.wallet || '').trim()
      if (nextWallet && !/^0x[a-fA-F0-9]{40}$/.test(nextWallet)) {
        setFeedback({ type: 'error', text: 'Invalid wallet format. Use a valid 0x address.' })
        return
      }
      const res = await updateMyProfile(formData, wallet || null)
      setUser(res.data.data)
      setIsEditing(false)
      setFeedback({ type: 'success', text: 'Profile updated successfully.' })

      // Dispatch event to update navbar if name changed
      window.dispatchEvent(
        new CustomEvent('profileUpdated', {
          detail: { user: res.data.data },
        })
      )
    } catch (error) {
      setFeedback({
        type: 'error',
        text: error.response?.data?.error || error.response?.data?.message || error.message,
      })
    } finally {
      setSaving(false)
    }
  }

  const handleCancel = () => {
    setFormData({
      name: user?.name || '',
      email: user?.email || '',
      wallet: user?.wallet || '',
    })
    setIsEditing(false)
  }

  const handleChangePassword = async (e) => {
    e.preventDefault()
    setPasswordFeedback(null)
    if (!passwordData.newPassword || passwordData.newPassword.length < 8) {
      setPasswordFeedback({ type: 'error', text: 'New password must be at least 8 characters.' })
      return
    }
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      setPasswordFeedback({ type: 'error', text: 'New password and confirmation do not match.' })
      return
    }
    try {
      setChangingPassword(true)
      await changeMyPassword(passwordData, wallet || null)
      setPasswordFeedback({ type: 'success', text: 'Password updated successfully.' })
      setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' })
    } catch (error) {
      setPasswordFeedback({
        type: 'error',
        text:
          error.response?.data?.error ||
          error.response?.data?.message ||
          'Failed to update password.',
      })
    } finally {
      setChangingPassword(false)
    }
  }

  if (loading) {
    return (
      <Section first eyebrow="Owner portal" title="Profile" subtitle="Loading your profile…">
        <div
          className="h-8 w-8 rounded-full border border-m1-line border-t-m1-violet"
          style={{ animation: 'm1nt-spin 0.9s linear infinite' }}
          role="status"
          aria-label="Loading your profile"
        />
      </Section>
    )
  }

  if (!wallet && !emailAuthToken) {
    return (
      <OwnerSignedOut
        eyebrow="Owner portal"
        title="Profile"
        subtitle="Log in to view and edit your profile."
        actions={
          <M1ntButton solid onClick={() => window.dispatchEvent(new Event('openAuthModal'))}>
            Log in
          </M1ntButton>
        }
        note="Sign in with your email or connect your wallet to manage your account details and password."
      />
    )
  }

  const initials = user?.name
    ? user.name.charAt(0).toUpperCase()
    : (wallet ? wallet.slice(2, 4) : (user?.email || '?').slice(0, 2)).toUpperCase()

  const fieldValue = (value, mono = false) =>
    value ? (
      <p className={`break-all text-[18px] text-m1-ink ${mono ? 'font-m1-mono text-[15px]' : ''}`}>
        {value}
      </p>
    ) : (
      <p className="text-[18px] font-light text-m1-muted">Not set</p>
    )

  const explorerLink = (address) => (
    <a
      href={`${getExplorerBaseUrl()}/address/${address}`}
      target="_blank"
      rel="noopener noreferrer"
      aria-label="View address on the block explorer"
      className="inline-flex h-8 w-8 flex-shrink-0 items-center justify-center text-m1-muted transition-colors hover:text-m1-violet"
    >
      <i className="ri-external-link-line text-lg" aria-hidden="true" />
    </a>
  )

  const feedbackNotice = (item, className = '') =>
    item?.text ? (
      <M1ntNotice
        tone={item.type === 'success' ? 'violet' : 'amber'}
        className={className}
        role={item.type === 'success' ? 'status' : 'alert'}
      >
        {item.text}
      </M1ntNotice>
    ) : null

  return (
    <>
      <Section
        first
        eyebrow="Owner portal"
        title="Profile"
        subtitle="Manage your account information."
        actions={
          <div className="flex flex-wrap gap-3">
            <M1ntButton solid onClick={() => navigate('/owner')}>
              My Cards
            </M1ntButton>
            <M1ntButton onClick={() => navigate('/owner', { state: { openSubmitModal: true } })}>
              Use M1NT credit
            </M1ntButton>
          </div>
        }
      >
        <div
          role="group"
          aria-label="Account summary"
          className="grid grid-cols-1 gap-px border border-m1-line bg-m1-line sm:grid-cols-3 lg:grid-cols-[1.5fr_1fr_1fr_1fr]"
        >
          <div className="flex items-center gap-4 bg-m1-ground p-5 sm:col-span-3 sm:p-6 lg:col-span-1">
            <div
              className="flex h-14 w-14 flex-shrink-0 items-center justify-center rounded-full border border-white/25 bg-m1-surface text-[20px] font-normal uppercase tracking-[0.04em] text-m1-ink"
              aria-hidden="true"
            >
              {initials}
            </div>
            <div className="min-w-0">
              <h2 className="truncate font-m1-display text-[26px] font-normal leading-tight tracking-[-0.01em] text-m1-ink">
                {user?.name || 'Anonymous User'}
              </h2>
              <p className="mt-2 inline-flex items-center gap-2 font-m1-mono text-[11px] uppercase tracking-[0.14em] text-m1-muted">
                <span className="h-1.5 w-1.5 rounded-full bg-m1-ice" aria-hidden="true" />
                {user?.role || 'OWNER'}
              </p>
            </div>
          </div>

          <div className="bg-m1-ground p-5 sm:p-6">
            <p className="font-m1-mono text-[11px] uppercase tracking-[0.14em] text-m1-muted">
              Signed in with
            </p>
            <p className="mt-3 truncate text-[18px] text-m1-ink">{wallet ? 'Wallet' : 'Email'}</p>
            {!wallet && user?.email && (
              <p className="mt-1 truncate text-[13px] text-m1-muted">{user.email}</p>
            )}
          </div>

          <div className="bg-m1-ground p-5 sm:p-6">
            <p className="font-m1-mono text-[11px] uppercase tracking-[0.14em] text-m1-muted">
              Wallet
            </p>
            {user?.wallet || wallet ? (
              <div className="mt-3 flex items-center gap-1">
                <span className="font-m1-mono text-[15px] text-m1-ink">
                  {formatAddress(user?.wallet || wallet)}
                </span>
                {explorerLink(user?.wallet || wallet)}
              </div>
            ) : (
              <p className="mt-3 text-[18px] font-light text-m1-muted">Not linked</p>
            )}
          </div>

          <div className="bg-m1-ground p-5 sm:p-6">
            <p className="font-m1-mono text-[11px] uppercase tracking-[0.14em] text-m1-muted">
              Member since
            </p>
            <p className="mt-3 text-[18px] text-m1-ink">
              {user?.createdAt ? formatDate(user.createdAt) : '—'}
            </p>
          </div>
        </div>
      </Section>

      <Section
        eyebrow="Account"
        title="Account details"
        actions={
          !isEditing ? (
            <M1ntButton onClick={() => setIsEditing(true)}>
              <i className="ri-edit-2-line text-lg" aria-hidden="true" />
              Edit
            </M1ntButton>
          ) : (
            <div className="flex flex-wrap gap-3">
              <M1ntButton onClick={handleCancel}>Cancel</M1ntButton>
              <M1ntButton
                onClick={handleSave}
                disabled={saving}
                className="border-m1-violet text-m1-violet"
              >
                {saving ? 'Saving…' : 'Save'}
              </M1ntButton>
            </div>
          )
        }
      >
        {feedbackNotice(feedback, 'mb-8')}

        <dl className="grid gap-x-10 gap-y-8 md:grid-cols-2">
          <div className="border-t border-m1-line pt-4">
            <dt className={M1NT_LABEL_CLASS}>Display name</dt>
            <dd>
              {isEditing ? (
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Enter your name"
                  className={M1NT_INPUT_CLASS}
                />
              ) : (
                fieldValue(user?.name)
              )}
            </dd>
          </div>

          <div className="border-t border-m1-line pt-4">
            <dt className={M1NT_LABEL_CLASS}>Email address</dt>
            <dd>
              {isEditing ? (
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  placeholder="Enter your email"
                  className={M1NT_INPUT_CLASS}
                />
              ) : (
                fieldValue(user?.email)
              )}
            </dd>
          </div>

          <div className="border-t border-m1-line pt-4 md:col-span-2">
            <dt className={M1NT_LABEL_CLASS}>Wallet address</dt>
            <dd>
              {isEditing ? (
                <input
                  type="text"
                  value={formData.wallet}
                  onChange={(e) => setFormData({ ...formData, wallet: e.target.value })}
                  placeholder="Enter your wallet address (0x...)"
                  className={`${M1NT_INPUT_CLASS} font-m1-mono text-[14px]`}
                />
              ) : user?.wallet ? (
                <div className="flex items-center gap-2">
                  {fieldValue(user.wallet, true)}
                  {explorerLink(user.wallet)}
                </div>
              ) : (
                <p className="text-[18px] font-light text-m1-muted">No wallet linked</p>
              )}
            </dd>
          </div>
        </dl>
      </Section>

      <Section eyebrow="Security" title="Change password" subtitle="Use at least 8 characters.">
        <form className="max-w-[32rem] space-y-6" onSubmit={handleChangePassword}>
          {feedbackNotice(passwordFeedback)}
          <div>
            <label htmlFor="profile-current-password" className={M1NT_LABEL_CLASS}>
              Current password
            </label>
            <input
              id="profile-current-password"
              type="password"
              value={passwordData.currentPassword}
              onChange={(e) =>
                setPasswordData((prev) => ({ ...prev, currentPassword: e.target.value }))
              }
              placeholder="Enter current password"
              autoComplete="current-password"
              className={M1NT_INPUT_CLASS}
            />
          </div>
          <div>
            <label htmlFor="profile-new-password" className={M1NT_LABEL_CLASS}>
              New password
            </label>
            <input
              id="profile-new-password"
              type="password"
              value={passwordData.newPassword}
              onChange={(e) =>
                setPasswordData((prev) => ({ ...prev, newPassword: e.target.value }))
              }
              placeholder="At least 8 characters"
              autoComplete="new-password"
              className={M1NT_INPUT_CLASS}
              required
            />
          </div>
          <div>
            <label htmlFor="profile-confirm-password" className={M1NT_LABEL_CLASS}>
              Confirm new password
            </label>
            <input
              id="profile-confirm-password"
              type="password"
              value={passwordData.confirmPassword}
              onChange={(e) =>
                setPasswordData((prev) => ({ ...prev, confirmPassword: e.target.value }))
              }
              placeholder="Re-enter new password"
              autoComplete="new-password"
              className={M1NT_INPUT_CLASS}
              required
            />
          </div>
          <M1ntButton
            type="submit"
            disabled={changingPassword}
            className="border-m1-violet text-m1-violet"
          >
            {changingPassword ? 'Updating…' : 'Update password'}
          </M1ntButton>
        </form>
      </Section>
    </>
  )
}
